/**
 * An Apollo link that tells the issue tracker what each GraphQL operation was.
 *
 * It sits at the head of a client's link chain and does two things, neither
 * of which changes what the app sees:
 *
 *   - when an operation fails (GraphQL errors in the response, or no usable
 *     response), it notes the operation's name, type and variables for the
 *     error objects (errorContext.ts). By the time one of them reaches the
 *     global error handler it is a bare object or an ApolloError that no
 *     longer says which operation it came from.
 *   - it leaves one entry per operation in the tracker's trail: name, type,
 *     variables, duration, and on failure the error code and message.
 *
 * The result and the error are passed on untouched, and nothing in here can
 * throw into the chain.
 */
import {
  ApolloLink,
  FetchResult,
  Observable,
  Operation,
} from '@apollo/client/core'
import { redactSecrets } from 'common/redact'
import { addErrorContext, OperationContext } from './errorContext'
import { codeOf, GraphQLErrorLike, isGraphQLErrorLike } from './graphqlErrors'
import { compactJson, issueTracker, IssueTracker } from './issueTracker'

/** variables in a trail entry; the `graphql` tab of an event has room for more */
const MAX_TRAIL_VARIABLES_LENGTH = 300
const MAX_TRAIL_MESSAGE_LENGTH = 300

/**
 * Operations whose successes are left out of the trail: the account check
 * rides on every other operation (AuthService.checkAccountActive) and would
 * fill the trail with itself.
 */
const QUIET_WHEN_SUCCESSFUL = ['selfStatus']

function describeOperation(operation: Operation): OperationContext {
  let type = 'query'
  operation.query.definitions.forEach((definition) => {
    if (definition.kind === 'OperationDefinition') {
      type = definition.operation
    }
  })
  return {
    name: operation.operationName || 'anonymous',
    type,
    variables: redactSecrets(operation.variables),
  }
}

function cut(text: string): string {
  return text.length > MAX_TRAIL_MESSAGE_LENGTH
    ? `${text.slice(0, MAX_TRAIL_MESSAGE_LENGTH - 1)}…`
    : text
}

function describeGraphQLError(error: GraphQLErrorLike): string {
  const message = typeof error.message === 'string' ? error.message : ''
  const code = codeOf(error)
  return cut(code ? `${code}: ${message}` : message)
}

function describeNetworkError(error: unknown): string {
  const { status, statusText, message } = (error || {}) as {
    status?: unknown
    statusText?: unknown
    message?: unknown
  }
  if (typeof status === 'number') {
    return cut(
      `HTTP ${status}${typeof statusText === 'string' ? ` ${statusText}` : ''}`,
    )
  }
  return cut(typeof message === 'string' && message ? message : 'no response')
}

/**
 * @param tracker only for specs; the default is the app's tracker
 */
export function createTrackingLink(
  tracker: Pick<IssueTracker, 'leaveBreadcrumb'> = issueTracker,
): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const started = Date.now()
    let entryLeft = false

    /**
     * Everything the tracker gets. `outcome` reads the result or the error;
     * it runs in here because nothing this link does may reach the app.
     */
    const record = (
      outcome: () => { failure: string | undefined; errors: unknown[] },
    ): void => {
      try {
        const { failure, errors } = outcome()
        const described = describeOperation(operation)
        if (failure !== undefined) {
          errors.forEach((error) =>
            addErrorContext(error, { operation: described }),
          )
        }
        if (entryLeft) {
          return
        }
        entryLeft = true
        if (
          failure === undefined &&
          QUIET_WHEN_SUCCESSFUL.indexOf(described.name) !== -1
        ) {
          return
        }
        tracker.leaveBreadcrumb(
          `${described.type} ${described.name}${
            failure === undefined ? '' : ' failed'
          }`,
          {
            variables: compactJson(
              described.variables,
              MAX_TRAIL_VARIABLES_LENGTH,
            ),
            ms: Date.now() - started,
            ...(failure === undefined ? {} : { error: failure }),
          },
          'request',
        )
      } catch (e) {
        // the operation itself is none of this link's business
      }
    }

    return new Observable<FetchResult>((observer) => {
      const subscription = forward(operation).subscribe({
        next: (result) => {
          record(() => {
            const errors =
              result && Array.isArray(result.errors) ? result.errors : []
            const first = errors.find(isGraphQLErrorLike)
            return first === undefined
              ? { failure: undefined, errors: [] }
              : {
                  failure: describeGraphQLError(first),
                  // the list is noted too: some call sites throw
                  // `result.errors` whole
                  errors: [result.errors, ...errors],
                }
          })
          observer.next(result)
        },
        error: (error) => {
          record(() => ({
            failure: describeNetworkError(error),
            errors: [error],
          }))
          observer.error(error)
        },
        complete: () => observer.complete(),
      })
      return () => subscription.unsubscribe()
    })
  })
}
